/**
 * Cancellation helpers for client fetches.
 *
 * A superseded or unmounted request is expected: aborting it is not an error.
 * `isAbortError` recognises that cancellation. A `TimeoutError` (from
 * `AbortSignal.timeout`) deliberately does NOT count: a request that took too
 * long is a real failure the caller should handle.
 *
 * `abortReason` builds the value for `controller.abort(reason)`. fetch rejects
 * with the reason object itself, so it must stay a DOMException named
 * `AbortError`; a plain Error or string would bypass every existing
 * `error.name === 'AbortError'` check. The message names the controller, so an
 * unhandled rejection in Sentry (for example from a browser extension that wraps
 * window.fetch and leaves its own promise chain unhandled) shows which request
 * was cancelled instead of "signal is aborted without reason".
 */
export function isAbortError(error: unknown, signal?: AbortSignal | null): boolean {
  if (error && typeof error === 'object' && (error as { name?: unknown }).name === 'AbortError') return true;
  return Boolean(signal?.aborted && error !== undefined && error === signal.reason);
}

export function abortReason(message: string): DOMException {
  return new DOMException(message, 'AbortError');
}
